import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GiftsService } from "@services/dws/gifts.service";
import { toast } from '@web/util/toast';
import { tr } from "@util/tr";
import { Gift } from '@interfaces/dws/gift';
import { ReservationsService } from '@services/reservations.service';

@Component({
  selector: 'app-gift-redeem',
  templateUrl: './gift-redeem.component.html',
  styleUrls: ['./gift-redeem.component.scss']
})
export class GiftRedeemComponent {
  giftId!: string;
  experienceId!: string;
  reservationId: string | null = null;
  showRedeemInput: boolean = false;
  isLoadingRedeem: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { gift: Gift },
    private dlg: MatDialogRef<GiftRedeemComponent>,
    private giftsService: GiftsService,
    private reservationService: ReservationsService
  ) {
    this.giftId = this.data.gift.giftId;
    this.experienceId = this.data.gift.extraData.experience.id;
  }

  closeClicked(): void {
    this.dlg.close();
  }

  onRedeemClick(): void {
    this.dlg.close({ action: 'link' });
  }

  onLinkClick(): void {
    this.showRedeemInput = true;
  }

  onConfirmRedeem(): void {
    this.isLoadingRedeem = true;
    // Check if a reservation id was provided and if it is valid
    if (this.reservationId && this.reservationId.trim() !== '') {
      this.reservationService.getCrm(this.reservationId).subscribe({
        next: () => {
          // Reservation id is valid, proceed with redemption
          this.redeemGift();
        },
        error: err => {
          this.isLoadingRedeem = false;
          if (err.status === 400) {
            toast(tr("The reservation id was invalid."));
          } else if (err.status === 404) {
            toast(tr("The reservation was not found."));
          } else {
            toast(tr("The reservation could not be found."));
          }
        }
      });
    } else {
      this.redeemGift();
    }
  }

  private redeemGift(): void {
    this.giftsService.redeem(this.giftId).subscribe({
      next: () => {
        if (this.reservationId && this.reservationId.trim() !== '') {
          // Reservation id was provided and is valid, proceed with association
          this.associateReservation(this.reservationId);
        } else {
          this.isLoadingRedeem = false;
          toast(tr("The gift has been redeemed."));
          this.dlg.close({ action: 'redeemed', reservationId: null });
        }
      },
      error: err => {
        this.isLoadingRedeem = false;
        console.error("Error redeeming gift", err);
        if (err.status === 404) {
          toast(tr("The gift was not found. It may have been removed or is no longer available."));
        } else {
          toast(tr("An error occurred while redeeming the gift. Please try again later."));
        }
      }
    });
  }

  associateReservation(reservationId: string): void {
    this.reservationService.attachGift(reservationId, this.giftId).subscribe({
      next: () => {
        this.isLoadingRedeem = false;
        toast(tr("The gift has been redeemed and attached to the reservation."));
        this.dlg.close({ action: 'redeemed', reservationId: reservationId });
      },
      error: err => {
        this.isLoadingRedeem = false;
        console.error("Error attaching gift to reservation", err);
        if (err.status === 400) {
          toast(tr("The gift was redeemed but the reservation id was invalid."));
        } else if (err.status === 404) {
          toast(tr("The gift was redeemed but the reservation was not found."));
        } else {
          toast(tr("The gift was redeemed but could not be attached to the reservation."));
        }
        this.dlg.close({ action: 'redeemed', reservationId: null });
      }
    });
  }

  onCancelRedeem(): void {
    this.showRedeemInput = false;
    this.reservationId = null;
  }
}